/**
 * F-148 §10.2 `/stock/receiving/drafts/:draftId`: header, scan bar, line cards, CSV import,
 * duplicate consolidation, autosave (2 s debounce + explicit Save) with a status chip, version
 * conflicts ("Changed in another window" + Reload), discard and Review. Scanning and edits change
 * local reducer state only; autosave persists them. Nothing here writes stock.
 *
 * Saves are serialized in this route (one queue for manual Save, autosave, Review and CSV import):
 * a caller that arrives while a save is in flight waits for it and then reads the latest state and
 * version, so one tab never produces a false version conflict. A failed save is never retried
 * automatically; it waits for a new edit or an explicit Retry (same token for the same payload).
 *
 * When a save fails with an unknown outcome (transport failure: the response was lost, so the
 * server may have committed it), the exact request is retained in memory. The next save first
 * re-sends it unchanged so the server either replays the committed version or applies it, then
 * the latest payload is sent at that version. A first-attempt `F148_STOCK_BUSY` is a definitive
 * rollback; during reconciliation it leaves the original outcome unknown, so the input is kept. This avoids a false
 * "changed in another window" after a lost response on a flaky connection.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileUp, Loader2, Plus, Trash2 } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import type { BarcodeLookupResult } from '@dukanti/shared';
import {
  useDiscardReceivingDraft,
  useImportReceivingRows,
  useReceivingAccess,
  useReceivingDraft,
  useReceivingSuppliers,
  useReceivingVariantSearch,
  useSaveReceivingDraft,
} from '@dukanti/shared/hooks';
import type {
  ReceivingCsvFileMeta,
  ReceivingCsvRow,
  ReceivingDraft,
  ReceivingScanLookup,
  ReceivingVariantSnapshot,
  SaveReceivingDraftInput,
} from '@dukanti/shared/admin';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useAdminBarcodeLookup } from '@/hooks/use-barcode-intake';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CsvImportDialog,
  DraftHeaderForm,
  DuplicateVariantBanner,
  ReceivingLineCard,
  ReceivingNotAvailable,
  ReceivingPausedBanner,
  ReceivingScanBar,
  SaveStatusChip,
  VariantPickerDialog,
  referenceValidationMessageKey,
} from '@/features/stock/receiving/components';
import {
  draftEditorReducer,
  hasUnsendableValues,
  initialDraftEditorState,
  savePayloadFingerprint,
  selectDraftIssues,
  selectDuplicateGroups,
  selectLineInputs,
  type DraftEditorAction,
} from '@/features/stock/receiving/lib/draft-reducer';
import {
  conflictCurrentVersion,
  getStockReceivingErrorInfo,
  isRetryableReceivingError,
  isVersionConflictError,
  issueMessageKey,
  receivingErrorKey,
  receivingErrorOptions,
  withRawValues,
} from '@/features/stock/receiving/lib/error-messages';
import {
  createIdempotencyKey,
  createSaveToken,
  idempotencyStore,
} from '@/features/stock/receiving/lib/idempotency-store';

const AUTOSAVE_DEBOUNCE_MS = 2000;
const HIGHLIGHT_MS = 1500;
const PRODUCT_IMAGES_BUCKET = 'product-images';

function productImageUrl(path: string): string | null {
  try {
    return supabase.storage.from(PRODUCT_IMAGES_BUCKET).getPublicUrl(path).data.publicUrl;
  } catch {
    return null;
  }
}

/**
 * True when the server may have committed the save although the client saw an error: the response
 * was lost in transport or a successful RPC response could not be decoded.
 * Server-reported codes, even retryable ones, mean the save was refused and never committed.
 */
function isAmbiguousSaveOutcome(err: unknown): boolean {
  const info = getStockReceivingErrorInfo(err);
  if (info?.code === 'F148_UNKNOWN' && info.details.cause === 'malformed_response') return true;
  if (!isRetryableReceivingError(err)) return false;
  return info === null || info.code === 'F148_UNKNOWN';
}

export function ReceivingDraftRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const { draftId = null } = useParams<{ draftId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const access = useReceivingAccess(supabase, storeId);
  const draftQuery = useReceivingDraft(supabase, draftId);

  const canManage = access.data?.canManage ?? false;
  const enabled = access.data?.enabled ?? false;

  return (
    <main
      dir={dir}
      data-testid="receiving-draft-route"
      className="mx-auto w-full max-w-5xl px-4 py-6 pb-28 sm:px-6 lg:px-8"
    >
      <Link
        to="/stock/receiving"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex min-h-11 items-center gap-1 text-sm"
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" />
        {t('admin.receiving.backToList')}
      </Link>

      {access.isLoading || (canManage && draftQuery.isLoading) ? (
        <div className="space-y-3" data-testid="receiving-draft-loading">
          <div className="bg-admin-surface-muted h-24 animate-pulse rounded-lg motion-reduce:animate-none" />
          <div className="bg-admin-surface-muted h-40 animate-pulse rounded-lg motion-reduce:animate-none" />
        </div>
      ) : null}

      {/* A failed access request is transient: never treated as "no permission" or "paused". */}
      {access.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-access-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void access.refetch()}
            data-testid="receiving-access-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && !canManage ? <ReceivingNotAvailable /> : null}

      {access.data && canManage && draftQuery.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-draft-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">
            {t(receivingErrorKey(draftQuery.error), receivingErrorOptions(draftQuery.error))}
          </p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void draftQuery.refetch()}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && canManage && draftQuery.data && storeId ? (
        <DraftEditor
          key={draftQuery.data.id}
          draft={draftQuery.data}
          storeId={storeId}
          enabled={enabled}
          refetch={() => void draftQuery.refetch()}
        />
      ) : null}
    </main>
  );
}

interface DraftEditorProps {
  readonly draft: ReceivingDraft;
  readonly storeId: string;
  readonly enabled: boolean;
  readonly refetch: () => void;
}

function DraftEditor({ draft, storeId, enabled, refetch }: DraftEditorProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, reactDispatch] = useReducer(draftEditorReducer, draft, initialDraftEditorState);
  // Mirror of the reducer state that is updated synchronously on dispatch. React batches renders,
  // so a queued save that runs right after the previous one resolves would otherwise read a state
  // (version, save status, pending token) that React has not rendered yet. The reducer is pure, so
  // applying each action here yields exactly the state React renders next. `dispatch` is the only
  // writer: every action goes through it, so the mirror is never rolled back by a render.
  const stateRef = useRef(state);
  const dispatch = useCallback((action: DraftEditorAction): void => {
    stateRef.current = draftEditorReducer(stateRef.current, action);
    reactDispatch(action);
  }, []);
  // Exact request of the last save whose outcome is unknown (retryable failure). Kept only in
  // memory for this draft editor; cleared on success, conflict, Reload, discard and unmount.
  const retainedSaveRef = useRef<SaveReceivingDraftInput | null>(null);
  useEffect(
    () => () => {
      retainedSaveRef.current = null;
    },
    [],
  );

  const editable = enabled && state.status === 'open';
  const issues = useMemo(() => selectDraftIssues(state), [state]);
  const duplicateGroups = useMemo(() => selectDuplicateGroups(state), [state]);
  const unsendable = useMemo(() => hasUnsendableValues(selectLineInputs(state)), [state]);
  // An invalid shipment reference is rejected server-side (`F148_VALIDATION`); block it locally
  // like unsendable quantities so autosave never loops on it. The header form shows the message.
  const referenceInvalid = referenceValidationMessageKey(state.header.shipmentReference) !== null;
  const sendBlocked = unsendable || referenceInvalid;

  // Server refetches → hydrate (merge while dirty/saving, replace otherwise). A replace drops the
  // local edits, so a retained request must never be replayed over the state the server returned.
  const hydrate = useCallback(
    (force = false): void => {
      const { dirty, saveStatus } = stateRef.current;
      const replaces = force || !(dirty || saveStatus === 'saving');
      dispatch({ type: 'hydrate', draft, force });
      if (replaces) retainedSaveRef.current = null;
    },
    [dispatch, draft],
  );
  const firstHydrateRef = useRef(true);
  useEffect(() => {
    if (firstHydrateRef.current) {
      firstHydrateRef.current = false;
      return;
    }
    hydrate();
  }, [hydrate]);

  // ---------------------------------------------------------------------------
  // Save (§3.2): one payload in flight, token reused only for the same payload.
  // Every caller (Save, autosave, Review, CSV import) goes through `runSave`, which chains behind
  // the save in flight so the next payload is built from the version that save returned.
  // ---------------------------------------------------------------------------
  const { save } = useSaveReceivingDraft(supabase);
  const saveQueueRef = useRef<Promise<boolean> | null>(null);
  const discardingRef = useRef(false);

  const performSave = useCallback(
    async (options: { skipIfClean?: boolean } = {}): Promise<boolean> => {
      const current = stateRef.current;
      if (
        discardingRef.current ||
        !enabled ||
        current.status !== 'open' ||
        current.saveStatus === 'conflict'
      )
        return false;
      // Autosave that was queued behind another save: nothing new to send, and a failed save is
      // never retried automatically (it waits for an edit or an explicit Retry).
      if (options.skipIfClean && (!current.dirty || current.saveStatus === 'failed')) {
        return current.saveStatus === 'saved';
      }
      const lines = selectLineInputs(current);
      if (hasUnsendableValues(lines)) return false;
      const header = current.header;
      if (referenceValidationMessageKey(header.shipmentReference) !== null) return false;
      const fingerprint = savePayloadFingerprint(header, lines);
      const token =
        current.pendingSave && current.pendingSave.fingerprint === fingerprint
          ? current.pendingSave.token
          : createSaveToken();
      dispatch({ type: 'saveStarted', token, fingerprint });
      const fail = (err: unknown): false => {
        dispatch({
          type: 'saveFailed',
          token,
          error: err,
          conflict: isVersionConflictError(err),
          conflictVersion: conflictCurrentVersion(err),
        });
        return false;
      };
      // Reconcile first: re-send a retained request unchanged (same token, version and payload) so
      // the server replays the committed version or applies it. When it is this very payload the
      // send below is the replay. Unresolved → stop here with the retained request kept; a version
      // conflict → another writer moved on, so only Reload helps. A retryable replay rejection may
      // precede token lookup, so it cannot settle the original outcome: keep it and stop too.
      const retained = retainedSaveRef.current;
      const replaying = retained !== null && retained.saveToken === token;
      if (retained && !replaying) {
        try {
          const replayed = await save(retained);
          retainedSaveRef.current = null;
          dispatch({ type: 'versionAdvanced', version: replayed.version });
        } catch (err) {
          if (isVersionConflictError(err)) retainedSaveRef.current = null;
          if (
            isVersionConflictError(err) ||
            isRetryableReceivingError(err) ||
            isAmbiguousSaveOutcome(err)
          )
            return fail(err);
          retainedSaveRef.current = null;
        }
      }
      const input: SaveReceivingDraftInput = {
        draftId: current.draftId,
        expectedVersion: stateRef.current.version,
        saveToken: token,
        header,
        lines,
      };
      try {
        const result = await save(input);
        retainedSaveRef.current = null;
        dispatch({ type: 'saveSucceeded', token, version: result.version });
        return true;
      } catch (err) {
        retainedSaveRef.current =
          isAmbiguousSaveOutcome(err) || (replaying && isRetryableReceivingError(err))
            ? input
            : null;
        return fail(err);
      }
    },
    [dispatch, enabled, save],
  );

  const runSave = useCallback(
    (options: { skipIfClean?: boolean } = {}): Promise<boolean> => {
      const previous = saveQueueRef.current;
      const run = (): Promise<boolean> => performSave(options);
      // The first save starts synchronously (so Save is disabled at once); later ones wait.
      const next = previous ? previous.then(run, run) : run();
      saveQueueRef.current = next;
      // Clear on either settlement so a rejection can neither pin the queue nor go unhandled.
      const clear = (): void => {
        if (saveQueueRef.current === next) saveQueueRef.current = null;
      };
      void next.then(clear, clear);
      return next;
    },
    [performSave],
  );

  /** Resolves once every queued save has settled (true when the last one succeeded). */
  const settleSaves = useCallback(async (): Promise<boolean> => {
    let ok = true;
    while (saveQueueRef.current) ok = await saveQueueRef.current;
    return ok;
  }, []);

  useEffect(() => {
    if (!state.dirty || !editable || sendBlocked) return;
    // `failed`/`conflict` wait for a new edit (→ `unsaved`), Retry or Reload: no automatic retry.
    if (state.saveStatus === 'failed' || state.saveStatus === 'conflict') return;
    const handle = setTimeout(() => void runSave({ skipIfClean: true }), AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [state.dirty, state.lines, state.header, state.saveStatus, editable, sendBlocked, runSave]);

  useEffect(() => {
    if (!state.dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [state.dirty]);

  // ---------------------------------------------------------------------------
  // Highlight (1.5 s)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!state.highlight) return;
    const handle = setTimeout(() => dispatch({ type: 'clearHighlight' }), HIGHLIGHT_MS);
    return () => clearTimeout(handle);
  }, [state.highlight, dispatch]);

  // ---------------------------------------------------------------------------
  // Suppliers (header)
  // ---------------------------------------------------------------------------
  const [supplierQuery, setSupplierQuery] = useState(draft.supplier.name);
  const suppliers = useReceivingSuppliers(supabase, storeId, supplierQuery);

  // ---------------------------------------------------------------------------
  // Scan (§4.7) — unchanged F045 lookup contract
  // ---------------------------------------------------------------------------
  const barcodeLookup = useAdminBarcodeLookup(storeId);
  const lookupRequestRef = useRef(0);
  const lookup = useCallback(
    (rawBarcode: string): Promise<BarcodeLookupResult> => {
      lookupRequestRef.current += 1;
      return barcodeLookup.mutateAsync({ rawBarcode, requestId: lookupRequestRef.current });
    },
    [barcodeLookup],
  );
  const onLookupResult = useCallback(
    (result: ReceivingScanLookup, captureId: string) => {
      dispatch({
        type: 'applyScan',
        lookup: result,
        captureId,
        newClientLineId: createIdempotencyKey(),
      });
    },
    [dispatch],
  );

  // ---------------------------------------------------------------------------
  // Variant picker (§4.6)
  // ---------------------------------------------------------------------------
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const search = useReceivingVariantSearch(supabase, pickerFor ? storeId : null, pickerQuery);
  const searchItems = useMemo(
    () => search.data?.pages.flatMap((page) => page.items) ?? [],
    [search.data],
  );
  const exactBarcode = search.data?.pages[0]?.exactBarcode ?? null;

  const openPicker = (clientLineId: string): void => {
    const line = state.lines.find((l) => l.clientLineId === clientLineId);
    setPickerQuery(line?.identifiers.barcode ?? line?.identifiers.supplierItemName ?? '');
    setPickerFor(clientLineId);
  };
  const onPickVariant = (variant: ReceivingVariantSnapshot): void => {
    if (pickerFor)
      dispatch({ type: 'setVariant', clientLineId: pickerFor, variant, matchMethod: 'manual' });
    setPickerFor(null);
  };

  // ---------------------------------------------------------------------------
  // CSV import (§4.4)
  // ---------------------------------------------------------------------------
  const [csvOpen, setCsvOpen] = useState(false);
  const importRows = useImportReceivingRows(supabase);
  const importTokensRef = useRef(new Map<string, string>());

  const onImport = async (
    rows: ReceivingCsvRow[],
    fileMeta: ReceivingCsvFileMeta,
    acknowledgeDuplicate: boolean,
  ) => {
    // Wait for any save in flight, then persist edits made meanwhile, so the import runs at the
    // latest version.
    await settleSaves();
    if (stateRef.current.dirty || stateRef.current.saveStatus === 'failed') {
      const saved = await runSave();
      if (!saved) throw stateRef.current.saveError ?? new Error('save_failed');
    }
    const key = `${fileMeta.fileName}:${fileMeta.byteSize}:${fileMeta.rowCount}:${stateRef.current.version}:${acknowledgeDuplicate}`;
    let token = importTokensRef.current.get(key);
    if (!token) {
      token = createSaveToken();
      importTokensRef.current.set(key, token);
    }
    const result = await importRows.mutateAsync({
      draftId: stateRef.current.draftId,
      expectedVersion: stateRef.current.version,
      saveToken: token,
      rows,
      fileMeta,
      acknowledgeDuplicate,
    });
    if (result.outcome === 'imported') {
      importTokensRef.current.clear();
      dispatch({ type: 'versionAdvanced', version: result.version });
    }
    return result;
  };

  // ---------------------------------------------------------------------------
  // Discard / Review
  // ---------------------------------------------------------------------------
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardPending, setDiscardPending] = useState(false);
  const discard = useDiscardReceivingDraft(supabase);
  const onDiscard = async (): Promise<void> => {
    if (discardingRef.current) return;
    discardingRef.current = true;
    setDiscardPending(true);
    try {
      // Wait for queued saves so the version sent is the one the server now holds: confirming
      // during an autosave must not surface a false version conflict.
      await settleSaves();
      // Resolve only the potentially committed request. Discard must not save newer edits or
      // require their local validity, but it must use the server version returned by this replay.
      const retained = retainedSaveRef.current;
      if (retained) {
        const replayed = await save(retained);
        retainedSaveRef.current = null;
        dispatch({ type: 'versionAdvanced', version: replayed.version });
      }
      const { draftId: id, version } = stateRef.current;
      await discard.mutateAsync({ draftId: id, expectedVersion: version });
      idempotencyStore.clearPostKeys(id);
      retainedSaveRef.current = null;
      toast.success(t('admin.receiving.draft.discarded'));
      navigate('/stock/receiving', { replace: true });
    } catch (err) {
      toast.error(t(receivingErrorKey(err), receivingErrorOptions(err)));
    } finally {
      discardingRef.current = false;
      setDiscardPending(false);
    }
  };
  const discardBusy = discardPending || discard.isPending;

  const [reviewPending, setReviewPending] = useState(false);
  const onReview = async (): Promise<void> => {
    setReviewPending(true);
    try {
      // Never review a draft with a save still in flight: the preview would be computed from the
      // previous version and posting would fail as stale.
      await settleSaves();
      if (stateRef.current.dirty || stateRef.current.saveStatus === 'failed') {
        const saved = await runSave();
        if (!saved) return;
      }
      if (stateRef.current.saveStatus === 'conflict') return;
      navigate(`/stock/receiving/drafts/${encodeURIComponent(state.draftId)}/review`);
    } finally {
      setReviewPending(false);
    }
  };

  const focusLine = (clientLineId: string): void => {
    dispatch({ type: 'highlight', clientLineId });
    const el = document.querySelector<HTMLElement>(`[data-client-line-id="${clientLineId}"]`);
    el?.focus();
  };

  // ---------------------------------------------------------------------------
  // Not open
  // ---------------------------------------------------------------------------
  if (state.status !== 'open') {
    return (
      <section
        role="status"
        className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-start"
        data-testid="receiving-draft-not-open"
      >
        <p className="text-foreground text-sm">{t('admin.receiving.draft.notOpen')}</p>
        {state.postedReceiptId ? (
          <Link
            to={`/stock/receiving/receipts/${encodeURIComponent(state.postedReceiptId)}`}
            className="text-foreground mt-3 inline-flex min-h-11 items-center underline underline-offset-4"
            data-testid="receiving-draft-open-receipt"
          >
            {t('admin.receiving.draft.openReceipt')}
          </Link>
        ) : null}
      </section>
    );
  }

  const blockingList = [
    ...issues.draftIssues
      .filter((i) => i.severity === 'blocking')
      .map((i) => ({
        key: `draft-${i.code}`,
        clientLineId: null as string | null,
        lineNo: null as number | null,
        text: t(issueMessageKey(i.code), i.meta),
      })),
    ...state.lines.flatMap((line) =>
      (issues.byLine.get(line.clientLineId) ?? [])
        .filter((i) => i.severity === 'blocking')
        .map((i, index) => ({
          key: `${line.clientLineId}-${i.code}-${i.field ?? ''}-${index}`,
          clientLineId: line.clientLineId,
          lineNo: line.lineNo,
          text: t(issueMessageKey(i.code), i.meta),
        })),
    ),
  ];

  return (
    <div className="space-y-6" data-testid="receiving-draft-editor" data-version={state.version}>
      <header className="flex flex-col gap-3 text-start sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
            {t('admin.receiving.draft.title')}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            <bdi>{state.supplierName}</bdi>
          </p>
        </div>
        <SaveStatusChip
          status={state.saveStatus}
          error={state.saveError}
          blockedByFields={sendBlocked}
          onRetry={() => void runSave()}
          onReload={() => {
            hydrate(true);
            refetch();
          }}
        />
      </header>

      {!enabled ? <ReceivingPausedBanner draftsReadOnly /> : null}

      <section className="border-border bg-card shadow-admin-card rounded-lg border p-4 sm:p-5">
        <DraftHeaderForm
          value={state.header}
          supplierName={state.supplierName}
          onChange={(patch, name) => {
            const { supplier, ...rest } = patch;
            dispatch({
              type: 'setHeader',
              patch: supplier ? { ...rest, supplier } : rest,
              supplierName: supplier ? name : undefined,
            });
          }}
          supplierQuery={supplierQuery}
          onSupplierQueryChange={setSupplierQuery}
          suppliers={suppliers.data ?? []}
          suppliersLoading={suppliers.isFetching}
          reference={state.reference}
          disabled={!editable}
        />
      </section>

      {blockingList.length > 0 ? (
        <section
          role="alert"
          aria-labelledby="draft-error-summary"
          className="border-destructive/40 bg-destructive/10 rounded-lg border p-4 text-start"
          data-testid="draft-error-summary"
        >
          <h2 id="draft-error-summary" className="text-destructive text-sm font-semibold">
            {t('admin.receiving.draft.errorSummary')}
          </h2>
          <ul className="mt-2 space-y-1 text-sm">
            {blockingList.slice(0, 20).map((item) => (
              <li key={item.key}>
                {item.clientLineId ? (
                  <button
                    type="button"
                    className="text-destructive min-h-8 text-start underline underline-offset-4"
                    onClick={() => focusLine(item.clientLineId as string)}
                  >
                    {t(
                      'admin.receiving.draft.errorSummaryItem',
                      withRawValues({ line: item.lineNo, message: item.text }),
                    )}
                  </button>
                ) : (
                  <span className="text-destructive">{item.text}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ReceivingScanBar
        lookup={lookup}
        onLookupResult={onLookupResult}
        feedback={state.lastScan}
        disabled={!editable}
      />

      <DuplicateVariantBanner
        groups={duplicateGroups}
        onCombine={(variantId) => dispatch({ type: 'consolidate', variantId })}
        disabled={!editable}
      />

      <section aria-labelledby="draft-lines-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="draft-lines-heading" className="font-display text-foreground text-lg">
            {t('admin.receiving.draft.lines')} <bdi dir="ltr">({state.lines.length})</bdi>
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={!editable || sendBlocked}
              onClick={() => setCsvOpen(true)}
              data-testid="draft-import-csv"
            >
              <FileUp aria-hidden="true" className="size-4" />
              {t('admin.receiving.draft.importCsv')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={!editable}
              onClick={() =>
                dispatch({ type: 'addManualLine', clientLineId: createIdempotencyKey() })
              }
              data-testid="draft-add-row"
            >
              <Plus aria-hidden="true" className="size-4" />
              {t('admin.receiving.draft.addRow')}
            </Button>
          </div>
        </div>

        {state.lines.length === 0 ? (
          <p
            className="border-border bg-card text-muted-foreground rounded-lg border border-dashed px-6 py-10 text-center text-sm"
            data-testid="draft-no-lines"
          >
            {t('admin.receiving.draft.noLines')}
          </p>
        ) : (
          <ul className="space-y-3">
            {state.lines.map((line) => (
              <li key={line.clientLineId}>
                <ReceivingLineCard
                  line={line}
                  issues={issues.byLine.get(line.clientLineId) ?? []}
                  checking={issues.checkingLineIds.has(line.clientLineId)}
                  highlighted={state.highlight?.clientLineId === line.clientLineId}
                  disabled={!editable}
                  imageUrlFor={productImageUrl}
                  onEditQuantity={(field, value) =>
                    dispatch({
                      type: 'editQuantity',
                      clientLineId: line.clientLineId,
                      field,
                      value,
                    })
                  }
                  onSetUnitKind={(unitKind) =>
                    dispatch({ type: 'setUnitKind', clientLineId: line.clientLineId, unitKind })
                  }
                  onConfirmPack={(confirmed) =>
                    dispatch({ type: 'confirmPack', clientLineId: line.clientLineId, confirmed })
                  }
                  onSelectVariant={() => openPicker(line.clientLineId)}
                  onClearVariant={() =>
                    dispatch({ type: 'clearVariant', clientLineId: line.clientLineId })
                  }
                  onResolveConflict={(variant) =>
                    dispatch({
                      type: 'setVariant',
                      clientLineId: line.clientLineId,
                      variant,
                      matchMethod: 'manual',
                    })
                  }
                  onSetIdentifier={(field, value) =>
                    dispatch({
                      type: 'setIdentifier',
                      clientLineId: line.clientLineId,
                      field,
                      value,
                    })
                  }
                  onSetNote={(note) =>
                    dispatch({ type: 'setNote', clientLineId: line.clientLineId, note })
                  }
                  onRemove={() => dispatch({ type: 'removeLine', clientLineId: line.clientLineId })}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Sticky bottom bar */}
      <div
        className="border-border bg-card/95 supports-[backdrop-filter]:bg-card/80 fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur"
        data-testid="draft-bottom-bar"
      >
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            {/* Visual duplicate of the header chip; the header instance is the live region. */}
            <SaveStatusChip status={state.saveStatus} blockedByFields={sendBlocked} live={false} />
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              disabled={discardBusy}
              onClick={() => setDiscardOpen(true)}
              data-testid="draft-discard"
            >
              <Trash2 aria-hidden="true" className="size-4" />
              {t('admin.receiving.draft.discard')}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={
                !editable ||
                state.saveStatus === 'saving' ||
                state.saveStatus === 'conflict' ||
                sendBlocked
              }
              onClick={() => void runSave()}
              data-testid="draft-save"
            >
              {t('admin.receiving.draft.save')}
            </Button>
            <Button
              type="button"
              className="min-h-11"
              disabled={
                !editable ||
                reviewPending ||
                issues.blockingCount > 0 ||
                issues.checkingLineIds.size > 0 ||
                sendBlocked ||
                state.saveStatus === 'conflict'
              }
              onClick={() => void onReview()}
              data-testid="draft-review"
            >
              {reviewPending ? (
                <Loader2
                  aria-hidden="true"
                  className="size-4 animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {t('admin.receiving.draft.review')}
            </Button>
          </div>
        </div>
      </div>

      <VariantPickerDialog
        open={pickerFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickerFor(null);
        }}
        query={pickerQuery}
        onQueryChange={setPickerQuery}
        items={searchItems}
        exactBarcode={exactBarcode}
        isLoading={search.isLoading}
        isError={search.isError}
        hasMore={search.hasNextPage}
        isLoadingMore={search.isFetchingNextPage}
        onLoadMore={() => void search.fetchNextPage()}
        onSelect={onPickVariant}
        imageUrlFor={productImageUrl}
      />

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        onImport={onImport}
        onImported={(count) => toast.success(t('admin.receiving.csv.imported', { count }))}
        currentLineCount={state.lines.length}
        disabled={!editable || sendBlocked}
      />

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent data-testid="discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.receiving.draft.discard')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.receiving.draft.discardConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11" disabled={discardBusy}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="min-h-11"
              disabled={discardBusy}
              onClick={(event) => {
                event.preventDefault();
                void onDiscard();
              }}
              data-testid="discard-confirm"
            >
              {t('admin.receiving.draft.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
